import { yupResolver } from "@hookform/resolvers/yup";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Controller, useForm } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import { InferType } from "yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { Alert, IconButton, InputAdornment, Stack } from "@mui/material";
import { useDispatch } from "react-redux";
import { showMessage } from "app/store/fuse/messageSlice";
import HTMLReactParser from "html-react-parser";
import FuseSplashScreen from "@fuse/core/FuseSplashScreen";
import FieldLabel from "../../client/editprofile/components/FieldLabel";
import CustomPrimaryButton from "@fuse/core/CustomPrimaryButton/CustomPrimaryButton";
import jwtService from "../services/jwtService";

const schema = yup.object().shape({
	password: yup
		.string()
		.max(255, "Password can't be more than 255 characters long")
		.required("Please enter your password.")
		.matches(
			/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*\?_\-])(?=.{8,})/,
			"Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character"
		),
	passwordConfirm: yup
		.string()
		.oneOf([yup.ref("password"), null], "Passwords must match"),
});

const defaultValues = {
	password: "",
	passwordConfirm: "",
};

function RecoverChangePasswordPage() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(true);
	const [showConfirmPassword, setShowConfirmPassword] = useState(true);
	const [customError, setCustomError] = useState("");

	useEffect(() => {
		const raw = sessionStorage.getItem("peeva_recover_flow");
		if (!raw) {
			navigate("/sign-up");
		}
	}, []);

	const { control, formState, handleSubmit, trigger } = useForm({
		mode: "onChange",
		defaultValues,
		resolver: yupResolver(schema),
	});

	const { errors } = formState;

	function getRecoverData() {
		const raw = sessionStorage.getItem("peeva_recover_flow");
		if (!raw) return null;
		try {
			return JSON.parse(raw) as { petId: string; petName?: string };
		} catch {
			return null;
		}
	}

	async function onSubmit({ password }: InferType<typeof schema>) {
		const data = getRecoverData();
		if (!data) {
			setCustomError("Session expired. Please start the recover process again.");
			return;
		}

		setLoading(true);
		try {
			const result = await jwtService.setPasswordByPetId(data.petId, password) as any;
			await jwtService.signInWithEmailAndPassword(result.email, password);
			sessionStorage.removeItem("peeva_recover_flow");
			dispatch(
				showMessage({
					message: "Password set successfully!",
					autoHideDuration: 5000,
				})
			);
			navigate("/client");
		} catch (err: any) {
			const msg = typeof err === "string" ? err : err?.message || "An error occurred.";
			setCustomError(msg);
			setLoading(false);
			setTimeout(() => setCustomError(""), 5000);
		}
	}

	return (
		<div className="flex flex-col bg-white justify-center w-full h-full md:overflow-hidden hide-scrollbar">
			{loading && <FuseSplashScreen />}
			<div className="h-full w-full flex md:flex-row flex-col overflow-hidden hide-scrollbar">
				<form
					name="recoverPasswordForm"
					noValidate
					className="justify-center md:basis-6/12 flex flex-col w-full h-full md:p-[40px_80px_20px_80px] p-[80px_20px_80px_20px] md:gap-[30px] gap-[20px] items-center"
					onSubmit={handleSubmit(onSubmit)}
				>
					<img
						className="h-[40px] w-[110.86px]"
						src="assets/img/checkout-logo.svg"
						alt="logo"
					/>
					{(() => {
						const petName = getRecoverData()?.petName || "Your pet";
						return (
							<div className="flex flex-col gap-[12px] w-full">
								<Typography className="text-2xl font-extrabold leading-tight">
									{petName}'s Microchip Is Now Active
								</Typography>
								<Typography variant="body2">
									Your contact information is now linked to this microchip in the Peeva recovery network.
								</Typography>
								<Typography variant="body2">
									If {petName} is scanned by a veterinarian, shelter, or animal control professional, your contact details will be visible so you can be reached quickly.
								</Typography>
								<Typography variant="body2">
									Create a password to access your Peeva account and manage your pet's information.
								</Typography>
							</div>
						);
					})()}
					<div className="flex flex-col w-full items-center">
						{customError && (
							<Stack className="pb-5" sx={{ width: "100%" }} spacing={2}>
								<Alert
									variant="filled"
									severity="error"
									onClose={() => setCustomError("")}
								>
									<p>{HTMLReactParser(customError)}</p>
								</Alert>
							</Stack>
						)}
						<div className="flex flex-col gap-[8px] w-full">
							<FieldLabel>Password</FieldLabel>
							<Controller
								name="password"
								control={control}
								render={({ field }) => (
									<TextField
										{...field}
										className="mb-20 bg-white rounded-24 h-[44px] md:h-46px text-14px md:text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
										type={showPassword ? "password" : "text"}
										autoFocus
										placeholder="Enter your password"
										error={!!errors.password}
										helperText={errors?.password?.message}
										required
										fullWidth
										variant="outlined"
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => setShowPassword(!showPassword)}
														edge="end"
													>
														{showPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											),
										}}
										sx={{ border: "2px solid black" }}
									/>
								)}
							/>
						</div>
						<div className="flex flex-col gap-[8px] w-full">
							<FieldLabel>Password (Confirm)</FieldLabel>
							<Controller
								name="passwordConfirm"
								control={control}
								render={({ field }) => (
									<TextField
										{...field}
										className="mb-20 bg-white rounded-24 h-[44px] md:h-46px text-14px md:text-16px font-400 md:font-700 leading-22px md:leading-22px text-black placeholder-black"
										type={showConfirmPassword ? "password" : "text"}
										error={!!errors.passwordConfirm}
										helperText={errors?.passwordConfirm?.message}
										placeholder="Enter your password (confirm)"
										variant="outlined"
										required
										autoComplete="off"
										fullWidth
										sx={{ border: "2px solid black" }}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="toggle password visibility"
														onClick={() => setShowConfirmPassword(!showConfirmPassword)}
														edge="end"
													>
														{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								)}
							/>
						</div>
					</div>

					<div className="flex flex-col gap-[16px] items-center">
						<CustomPrimaryButton
							onClick={() => trigger()}
						>
							Save Password
						</CustomPrimaryButton>
					</div>
				</form>
				<div className="md:max-h-[100vh] flex justify-between flex-col h-full w-full md:pb-0 md:w-1/2 overflow-hidden pb-0 px-0 sm:shadow bg-no-repeat bg-top object-fill md:bg-center bg-contain md:bg-cover">
					<img className="object-fill" src="assets/img/signupIcon7.jpg" />
				</div>
			</div>
		</div>
	);
}

export default RecoverChangePasswordPage;
